import { PlayerSeatDto, Street, TableState } from '@/services/table/tableState';
import { BettingStructure } from '@/services/common/types/bettingStructure';

interface PreAction {
  label?: string;
  amount?: number; // Optional field for actions involving an amount
}
interface PreActions {
  foldCheck?: PreAction;
  checkCall?: PreAction;
  betRaise?: PreAction;
  standPatOrDiscard?: PreAction;
}

const toFloat = (amount: number | undefined) => {
  if (amount === undefined) return undefined;
  return parseFloat(amount.toFixed(2));
};
export default function getPreActions(
  tableState: TableState,
  player: PlayerSeatDto,
): {
  canBet?: boolean;
  canCheck?: boolean;
  canFold?: boolean;
  canStandPatOrDiscard?: boolean;
} | null {
  const { currentVariant, handState } = tableState;
  if (!handState) {
    return null;
  }
  const { state } = handState;
  if (!state || !state.status || (player.playerIndex && !state.statuses[player.playerIndex])) {
    return null;
  }
  // True if we are in someone's turn
  const isPlaying = state.actor_index !== null || state.stander_pat_or_discarder_index !== null;
  const indices = [state.actor_index, state.showdown_index, state.stander_pat_or_discarder_index];

  const isActingPlayer = indices.some((index) => index !== null && index === player.playerIndex);

  const isPlayerAllin = player.playerIndex && state.stacks[player.playerIndex] === 0;

  if (!state.status || (player.playerIndex && !state.statuses[player.playerIndex]) || !isPlaying || isPlayerAllin) {
    return null;
  }
  const isFixedLimit = currentVariant.bettingStructure === BettingStructure.FIXED_LIMIT;
  const playerBet = player.playerIndex ? state.bets[player.playerIndex] : undefined;
  const maxBet = Math.max(...state.bets);
  const hasBet = playerBet !== maxBet;
  const currentStreet: Street | undefined =
    state.street_index !== undefined ? state.streets[state.street_index] : undefined;
  const playerAlreadyDiscarded = false;
  const someoneIsAllIn = state.stacks.some((stack: number, index: number) => stack === 0 && state.bets[index] > 0);
  const callAmount = maxBet - playerBet!;
  const playerHasEnoughToCall = callAmount <= player.stack;
  const canRaise =
    state?.max_completion_betting_or_raising_to_amount && state?.max_completion_betting_or_raising_to_amount > 0;
  if (currentStreet?.draw_status && !playerAlreadyDiscarded) {
    return {
      canStandPatOrDiscard: true,
    };
  }
  let foldCheck: PreAction | undefined;
  let checkCall: PreAction | undefined;
  let betRaise: PreAction | undefined;
  if (isFixedLimit) {
    // Not facing a bet
    if (!hasBet) {
      foldCheck = canRaise ? { label: 'Check/Fold' } : undefined;
      checkCall = canRaise ? { label: 'Check/Call' } : undefined;
      betRaise = canRaise ? { label: 'Raise Any' } : undefined;
      // Facing an all-in bet
    } else if (hasBet && someoneIsAllIn) {
      foldCheck = { label: 'Fold' };
      checkCall =
        callAmount > 0
          ? playerHasEnoughToCall
            ? {
                label: `Call $${toFloat(callAmount)}`,
                amount: toFloat(callAmount),
              }
            : { label: 'Call All-in', amount: toFloat(player.stack) }
          : undefined;
      betRaise = state.bets.some(
        (bet: number, index: number) => bet > 0 && bet < state.stacks[index] && index !== player.playerIndex,
      )
        ? { label: 'Raise Any' }
        : undefined;
      // Facing a regular bet
    } else {
      foldCheck = { label: 'Fold' };
      checkCall =
        callAmount > 0
          ? playerHasEnoughToCall
            ? {
                label: `Call $${toFloat(callAmount)}`,
                amount: toFloat(callAmount),
              }
            : { label: 'Call All-in', amount: toFloat(player.stack) }
          : undefined;
      betRaise = canRaise ? { label: 'Raise Any' } : undefined;
    }
  } else {
    // Not facing a bet
    if (!hasBet) {
      foldCheck = { label: 'Check/Fold' };
      checkCall = { label: 'Check' };
      // Facing an all-in bet
    } else if (hasBet && someoneIsAllIn) {
      foldCheck = { label: 'Fold' };
      checkCall =
        callAmount > 0
          ? playerHasEnoughToCall
            ? {
                label: `Call $${toFloat(callAmount)}`,
                amount: toFloat(callAmount),
              }
            : { label: 'Call All-in', amount: toFloat(player.stack) }
          : undefined;
      // Facing a regular bet
    } else {
      foldCheck = { label: 'Fold' };
      checkCall =
        callAmount > 0
          ? playerHasEnoughToCall
            ? {
                label: `Call $${toFloat(callAmount)}`,
                amount: toFloat(callAmount),
              }
            : { label: 'Call All-in', amount: toFloat(player.stack) }
          : undefined;
    }
  }
  return {
    canFold: foldCheck !== undefined,
    canCheck: checkCall !== undefined,
    canBet: betRaise !== undefined,
  };
}
